const groups: [string, [string, string][]][] = [
    ['Symbols', [
        ['©', 'Copyright'], ['®', 'Registered trademark'], ['™', 'Trademark'], ['§', 'Section'],
        ['¶', 'Paragraph'], ['°', 'Degree'], ['•', 'Bullet'], ['…', 'Ellipsis'],
        ['–', 'En dash'], ['—', 'Em dash'], ['‘', 'Left single quotation mark'], ['’', 'Right single quotation mark'],
        ['“', 'Left double quotation mark'], ['”', 'Right double quotation mark'],
        ['✓', 'Check mark'], ['✗', 'Cross mark'], ['★', 'Star'], ['♥', 'Heart'],
        ['€', 'Euro'], ['£', 'Pound'], ['¥', 'Yen'], ['¢', 'Cent'],
        ['←', 'Left arrow'], ['↑', 'Up arrow'], ['→', 'Right arrow'], ['↓', 'Down arrow'], ['↔', 'Left right arrow'],
    ]],
    ['Mathematics', [
        ['±', 'Plus minus'], ['×', 'Multiplication'], ['÷', 'Division'], ['−', 'Minus'],
        ['≠', 'Not equal'], ['≈', 'Approximately equal'], ['≤', 'Less than or equal'], ['≥', 'Greater than or equal'],
        ['∞', 'Infinity'], ['√', 'Square root'], ['∑', 'Summation'], ['∫', 'Integral'],
        ['π', 'Pi'], ['α', 'Alpha'], ['β', 'Beta'], ['θ', 'Theta'], ['Δ', 'Delta'], ['Ω', 'Omega'],
        ['½', 'One half'], ['¼', 'One quarter'], ['¾', 'Three quarters'], ['²', 'Superscript two'], ['³', 'Superscript three'],
    ]],
    ['Accented letters', [
        ['á', 'A acute'], ['à', 'A grave'], ['â', 'A circumflex'], ['ä', 'A diaeresis'], ['å', 'A ring'],
        ['æ', 'AE ligature'], ['ç', 'C cedilla'], ['é', 'E acute'], ['è', 'E grave'], ['ê', 'E circumflex'],
        ['ë', 'E diaeresis'], ['í', 'I acute'], ['ï', 'I diaeresis'], ['ñ', 'N tilde'], ['ó', 'O acute'],
        ['ö', 'O diaeresis'], ['ø', 'O stroke'], ['œ', 'OE ligature'], ['ß', 'Sharp S'], ['ú', 'U acute'], ['ü', 'U diaeresis'],
    ]],
    ['Emoji', [
        ['😀', 'Grinning face'], ['😊', 'Smiling face'], ['😂', 'Face with tears of joy'], ['🤔', 'Thinking face'],
        ['😎', 'Smiling face with sunglasses'], ['🥳', 'Partying face'], ['👍', 'Thumbs up'], ['👎', 'Thumbs down'],
        ['👏', 'Clapping hands'], ['🙌', 'Raising hands'], ['👋', 'Waving hand'], ['💪', 'Flexed biceps'],
        ['❤️', 'Red heart'], ['✅', 'Check mark button'], ['❌', 'Cross mark emoji'], ['⚠️', 'Warning'],
        ['🎉', 'Party popper'], ['🏆', 'Trophy'], ['🎯', 'Target bullseye'], ['💡', 'Light bulb'],
        ['📚', 'Books'], ['📝', 'Memo'], ['🔍', 'Magnifying glass'], ['🚀', 'Rocket'], ['🌍', 'Globe'],
        ['👩‍💻', 'Woman technologist'], ['👍🏽', 'Thumbs up medium skin tone'], ['🇬🇧', 'United Kingdom flag'],
    ]],
    ['Letters', [
        ['A', 'Latin capital letter A'], ['Ж', 'Cyrillic capital letter Zhe'],
        ['א', 'Hebrew letter Alef'], ['あ', 'Hiragana letter A'],
    ]],
    ['Numbers', [
        ['①', 'Circled digit one'], ['②', 'Circled digit two'],
        ['Ⅳ', 'Roman numeral four'], ['Ⅷ', 'Roman numeral eight'],
    ]],
    ['Punctuation', [
        ['¡', 'Inverted exclamation mark'], ['¿', 'Inverted question mark'],
        ['«', 'Left double angle quotation mark'], ['»', 'Right double angle quotation mark'],
    ]],
    ['Combining marks', [
        ['\u0301', 'Combining acute accent'], ['\u0300', 'Combining grave accent'],
        ['\u0308', 'Combining diaeresis'], ['\u0327', 'Combining cedilla'],
    ]],
    ['Spaces', [
        ['\u00A0', 'No-break space'], ['\u2002', 'En space'],
        ['\u2003', 'Em space'], ['\u2009', 'Thin space'],
    ]],
];

export interface SpecialCharacter { category: string; value: string; name: string; searchText?: string }

// A small, curated starting palette. Searches use the full catalog below.
export const specialCharacters: SpecialCharacter[] = groups.flatMap(([category, entries]) => entries.map(([value, name]) => ({ category, value, name })));

export const characterCategories = ['All', 'Symbols', 'Mathematics', 'Accented letters', 'Emoji', 'Letters', 'Numbers', 'Punctuation', 'Combining marks', 'Spaces'];

interface CharacterCatalog {
    entries: [string, string, string][];
    ranges: [number, number, string, string][];
}

let catalogRequest: Promise<SpecialCharacter[]> | null = null;

export function loadCharacterCatalog(): Promise<SpecialCharacter[]> {
    if (!catalogRequest) {
        catalogRequest = Promise.resolve().then(() => fetch(`${process.env.PUBLIC_URL || ''}/assets/unicode/catalog-v18.json`))
            .then(response => {
                if (!response.ok) throw new Error('Character catalog could not be loaded.');
                return response.json() as Promise<CharacterCatalog>;
            }).then(data => {
                const common = new Map(specialCharacters.map(item => [item.value, item]));
                const characters = new Map(common);
                const add = (value: string, name: string, category: string) => {
                    const preferred = common.get(value);
                    const previousSearch = characters.get(value)?.searchText ?? '';
                    characters.set(value, {
                        value, name: preferred?.name ?? name, category: preferred?.category ?? category,
                        searchText: `${previousSearch} ${name} ${preferred?.name ?? ''} ${value} ${unicodeCodePoints(value)}`.toLowerCase(),
                    });
                };
                for (const [value, name, category] of data.entries) add(value, name, category);
                for (const [start, end, name, category] of data.ranges) {
                    for (let point = start; point <= end; point++) {
                        add(String.fromCodePoint(point), name.replace('*', point.toString(16).toUpperCase().padStart(4, '0')), category);
                    }
                }
                return Array.from(characters.values());
            }).catch(error => {
                catalogRequest = null; // A failed download can be retried.
                throw error;
            });
    }
    return catalogRequest;
}

export function searchCharacters(characters: SpecialCharacter[], query: string, category = 'All'): SpecialCharacter[] {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return characters.filter(item => {
        if (category !== 'All' && item.category !== category) return false;
        if (!terms.length) return true;
        const text = item.searchText ?? `${item.name} ${item.value} ${unicodeCodePoints(item.value)}`.toLowerCase();
        return terms.every(term => text.includes(term));
    });
}

export function unicodeCodePoints(value: string): string {
    return Array.from(value, char => `U+${char.codePointAt(0)!.toString(16).toUpperCase().padStart(4, '0')}`).join(' ');
}
